"use client"

import React, { useEffect, useState } from "react"
import { DatePicker, Spin } from "antd"
import dayjs from "dayjs"
import DropdownFilter from "../../components/Common/DropdownFilter"
import { Analytics } from "../../Apis/analyticsApi"

// 🌍 Country name formatter (auto switch to Arabic if locale is ar)
const getCountryNameFormatter = (lang) => {
  try {
    return new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
      type: "region",
    })
  } catch {
    return null
  }
}

const getCountryName = (code, formatter) => {
  if (!code) return "Unknown"
  try {
    return formatter?.of(code.toUpperCase()) || code.toUpperCase()
  } catch {
    return code.toUpperCase()
  }
}

const DropdownWithDatePicker = ({ currentLanguage, onFilterSelect }) => {
  const lang = localStorage.getItem("language") || "en"
  const { RangePicker } = DatePicker

  const [selectedDaysLabel, setSelectedDaysLabel] = useState(
    lang === "ar" ? "حتى التاريخ" : "Till Date"
  )
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  })
  const [tillDate, setTillDate] = useState(() => dayjs("2025-01-01"))
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedCountries, setSelectedCountries] = useState([])
  const [countries, setCountries] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(false)

  // ✅ Initialize country name formatter based on language
  const countryNameFormatter = getCountryNameFormatter(lang)

  // ✅ Fetch countries dynamically from backend
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true)
      try {
        const response = await Analytics.getUserPaymentSummary()
        if (response.status === 200 && response.data?.available_countries) {
          const apiCountries = response.data.available_countries.map(
            (code, index) => {
              const upperCode = String(code).toUpperCase()
              return {
                id: index + 1,
                value: upperCode,
                label: getCountryName(upperCode, countryNameFormatter),
                flag: `https://flagcdn.com/${upperCode.toLowerCase()}.svg`,
              }
            }
          )
          setCountries(apiCountries)
        } else {
          setCountries([])
        }
      } catch (error) {
        console.error("Error fetching countries:", error)
        setCountries([])
      } finally {
        setLoadingCountries(false)
      }
    }

    fetchCountries()
  }, [lang])

  // ✅ Default Till Date range on mount
  useEffect(() => {
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"
    const januaryFirst2025 = dayjs("2025-07-01")
    const defaultRange = {
      startDate: "2025-07-01",
      endDate: dayjs().format("YYYY-MM-DD"),
      filterType: "tilldate",
    }
    setCustomDateRange(defaultRange)
    setTillDate(januaryFirst2025)
    onFilterSelect({ ...defaultRange, label: tillDateLabel, country: [] })
  }, [])

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".country-count")) setShowDropdown(false)
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  // ✅ Date Range logic
  const getDateRange = (label) => {
    const today = dayjs()
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"

    switch (label) {
      case tillDateLabel:
      case "Till Date":
        // If tillDate is set, use it; otherwise default to 1 year ago
        const startDate = tillDate
          ? dayjs(tillDate).format("YYYY-MM-DD")
          : today.subtract(1, "year").format("YYYY-MM-DD")
        return {
          startDate: startDate,
          endDate: today.format("YYYY-MM-DD"),
          filterType: "tilldate",
        }
      case "Last 7 days":
      case "آخر ٧ أيام":
        return {
          startDate: today.subtract(7, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last7days",
        }
      case "Last 30 days":
      case "آخر ٣٠ يومًا":
        return {
          startDate: today.subtract(30, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last30days",
        }
      case "Last 90 days":
      case "آخر ٩٠ يومًا":
        return {
          startDate: today.subtract(90, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last90days",
        }
      default:
        return { startDate: null, endDate: null, filterType: "customdate" }
    }
  }

  // ✅ Toggle country selection
  const toggleCountry = (value) => {
    const updated = selectedCountries.includes(value)
      ? selectedCountries.filter((c) => c !== value)
      : [...selectedCountries, value]
    const unique = [...new Set(updated)]
    setSelectedCountries(unique)

    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"

    let currentRange
    if (
      selectedDaysLabel === customRange ||
      selectedDaysLabel === "Custom Range"
    ) {
      currentRange = {
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate,
        filterType: "customdate",
      }
    } else if (
      selectedDaysLabel === tillDateLabel ||
      selectedDaysLabel === "Till Date"
    ) {
      // Use tillDate if available, otherwise use customDateRange
      const dateToUse =
        tillDate ||
        (customDateRange.startDate
          ? dayjs(customDateRange.startDate)
          : dayjs().subtract(1, "year"))
      currentRange = {
        startDate: dateToUse.format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
        filterType: "tilldate",
      }
    } else {
      currentRange = getDateRange(selectedDaysLabel)
    }

    onFilterSelect({
      ...currentRange,
      label: selectedDaysLabel,
      country: unique,
    })
  }

  const handleDropdownChange = (value) => {
    setSelectedDaysLabel(value)
    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"
    const tillDateLabel = lang === "ar" ? "حتى التاريخ" : "Till Date"

    // ✅ If Custom Range is selected, don't call API yet
    if (value === customRange || value === "Custom Range") {
      setCustomDateRange({ startDate: null, endDate: null })
      return
    }

    // ✅ If Till Date is selected, set default to 1 year ago if not already set
    if (value === tillDateLabel || value === "Till Date") {
      const oneYearAgo = dayjs().subtract(1, "year")
      const dateToUse = tillDate || oneYearAgo
      if (!tillDate) {
        setTillDate(oneYearAgo)
      }
      const range = {
        startDate: dateToUse.format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
        filterType: "tilldate",
      }
      setCustomDateRange(range)
      onFilterSelect({
        ...range,
        label: value,
        country: selectedCountries,
      })
      return
    }

    // ✅ For predefined ranges, call API immediately
    const range = getDateRange(value)
    setCustomDateRange(range)
    onFilterSelect({ ...range, label: value, country: selectedCountries })
  }

  const handleCustomRangeChange = (dates) => {
    if (dates?.length === 2) {
      const [start, end] = dates
      const range = {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        filterType: "customdate",
        label: lang === "ar" ? "نطاق مخصص" : "Custom Range",
      }
      setCustomDateRange(range)
      onFilterSelect({ ...range, country: selectedCountries })
    }
  }

  const handleTillDateChange = (date) => {
    if (date) {
      const today = dayjs()
      setTillDate(date)
      const range = {
        startDate: date.format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
        filterType: "tilldate",
        label: lang === "ar" ? "حتى التاريخ" : "Till Date",
      }
      setCustomDateRange(range)
      onFilterSelect({
        ...range,
        country: selectedCountries,
      })
    }
  }

  // Disable future dates for Custom Range
  const disabledDateForCustomRange = (current) => {
    return current && current > dayjs().endOf("day")
  }

  // Disable dates before 1 year ago and after today for Till Date
  const disabledDateForTillDate = (current) => {
    if (!current) return false
    const today = dayjs().endOf("day")
    const oneYearAgo = dayjs().subtract(1, "year").startOf("day")
    return current > today || current < oneYearAgo
  }

  // 🌐 Arabic or English dropdown options
  const dropdownOptions =
    lang === "ar"
      ? [
          { label: "حتى التاريخ", value: "حتى التاريخ" },
          { label: "آخر ٧ أيام", value: "آخر ٧ أيام" },
          { label: "آخر ٣٠ يومًا", value: "آخر ٣٠ يومًا" },
          { label: "آخر ٩٠ يومًا", value: "آخر ٩٠ يومًا" },
          { label: "نطاق مخصص", value: "نطاق مخصص" },
        ]
      : [
          { label: "Till Date", value: "Till Date" },
          { label: "Last 7 days", value: "Last 7 days" },
          { label: "Last 30 days", value: "Last 30 days" },
          { label: "Last 90 days", value: "Last 90 days" },
          { label: "Custom Range", value: "Custom Range" },
        ]

  return (
    <div
      className="filter-section"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        position: "relative",
        zIndex: 100,
        direction: lang === "ar" ? "rtl" : "ltr",
      }}
    >
      {/* ✅ Date Picker for Custom Range */}
      {selectedDaysLabel === (lang === "ar" ? "نطاق مخصص" : "Custom Range") && (
        <RangePicker
          allowClear={false}
          disabledDate={disabledDateForCustomRange}
          onChange={handleCustomRangeChange}
          style={{ height: 40, marginRight: "10px" }}
        />
      )}

      {/* ✅ Date Picker for Till Date - Hidden (date is set automatically) */}
      {/* {(selectedDaysLabel === "Till Date" ||
        selectedDaysLabel === "حتى التاريخ") && (
        <DatePicker
          allowClear={false}
          disabledDate={disabledDateForTillDate}
          onChange={handleTillDateChange}
          value={tillDate ? dayjs(tillDate) : dayjs().subtract(1, "year")}
          style={{ height: 40, marginRight: "10px" }}
          placeholder={lang === "ar" ? "اختر التاريخ" : "Select Date"}
        />
      )} */}

      {/* ✅ Date Range Dropdown */}
      <DropdownFilter
        selectedCategory={selectedDaysLabel}
        width="182px"
        label={lang === "ar" ? "حتى التاريخ" : "Till Date"}
        iconSrc="/icons/selectstoreanalytics.svg"
        options={dropdownOptions}
        onSelect={handleDropdownChange}
      />

      {/* ✅ Country Selector */}
      <div
        className="country-count"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          position: "relative",
        }}
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <img src="/dashboard/global.svg" alt="Global" width={20} height={20} />
        {selectedCountries.length > 0 && (
          <span>({selectedCountries.length})</span>
        )}

        {showDropdown && (
          <div className="zory-country-dropdown">
            {loadingCountries ? (
              <div className="zory-loader-container">
                <Spin size="small" />
              </div>
            ) : countries.length > 0 ? (
              <>
                {/* Selected Tags */}
                {selectedCountries.length > 0 && (
                  <div className="zory-selected-tags">
                    {selectedCountries.map((code) => {
                      const country = countries.find((c) => c.value === code)
                      return (
                        <div className="zory-country-tag" key={code}>
                          {country?.label}
                          <span
                            className="zory-remove-tag"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleCountry(code)
                            }}
                          >
                            ✕
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Country List */}
                <div className="zory-country-list">
                  {countries.map((c) => {
                    const isSelected = selectedCountries.includes(c.value)
                    return (
                      <div
                        key={c.id}
                        className={`zory-country-item ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleCountry(c.value)
                        }}
                      >
                        <div className="zory-country-left">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="zory-country-checkbox"
                          />
                          <span className="zory-country-label">{c.label}</span>
                        </div>
                        <img
                          src={c.flag}
                          alt={c.label}
                          className="zory-country-flag"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <p className="zory-no-country">
                {lang === "ar" ? "لا توجد دول" : "No countries found"}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DropdownWithDatePicker
